// ============================================================================
// src/stores/InvoiceStore.ts - INVOICE MANAGEMENT STORE
// ============================================================================

import { makeAutoObservable, runInAction } from 'mobx';
import { 
  Invoice, 
  InvoiceFilters,
  InvoiceGenerationOptions,
  CreateInvoiceData 
} from '@/types/invoices';
import { invoiceService } from '@/services/invoiceService';
import { RootStore } from '.';

/**
 * InvoiceStore - Quản lý hóa đơn và VAT
 * 
 * Features:
 * - Auto-generate invoice từ payment
 * - Query và filter invoices
 * - Download PDF
 * - Resend email
 * - Statistics và analytics
 */
export class InvoiceStore {
  rootStore: RootStore;

  // ========================================
  // INVOICE DATA
  // ========================================
  invoices: Invoice[] = [];
  selectedInvoice: Invoice | null = null;
  
  // ========================================
  // UI STATES
  // ========================================
  isLoading: boolean = false;
  isGenerating: boolean = false;
  isDownloading: boolean = false;
  isSendingEmail: boolean = false;
  error: string | null = null;

  // ========================================
  // PAGINATION
  // ========================================
  currentPage: number = 1;
  pageSize: number = 20;
  totalInvoices: number = 0;
  totalPages: number = 1;
  hasMore: boolean = true;

  // ========================================
  // FILTERS
  // ========================================
  filters: InvoiceFilters = {};

  // ========================================
  // STATISTICS
  // ========================================
  stats: {
    totalInvoices: number;
    totalAmount: number;
    paidAmount: number;
    unpaidAmount: number;
    thisMonthInvoices: number;
    thisMonthAmount: number;
  } | null = null;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
  }

  // ========================================
  // INITIALIZATION
  // ========================================

  /**
   * Initialize store - Load user's invoices
   */
  async initialize() {
    await this.loadMyInvoices();
    await this.loadStats();
  }

  // ========================================
  // LOAD INVOICES
  // ========================================

  /**
   * Load invoices cho user hiện tại
   */
  async loadMyInvoices(page: number = 1) {
    this.setLoading(true);
    this.setError(null);

    try {
      const result = await invoiceService.getMyInvoices(
        this.filters,
        page,
        this.pageSize
      );

      runInAction(() => {
        if (page === 1) {
          this.invoices = result.invoices;
        } else {
          // Append for pagination
          this.invoices = [...this.invoices, ...result.invoices];
        }

        this.currentPage = result.pagination.page;
        this.totalPages = result.pagination.pageCount;
        this.totalInvoices = result.pagination.total;
        this.hasMore = result.pagination.page < result.pagination.pageCount;
      });

      console.log('✅ Loaded invoices:', result.invoices.length);
    } catch (error: any) {
      this.setError(error.message || 'Không thể tải danh sách hóa đơn');
      console.error('Load invoices error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Load more invoices (pagination)
   */
  async loadMoreInvoices() {
    if (!this.hasMore || this.isLoading) {
      return;
    }

    await this.loadMyInvoices(this.currentPage + 1);
  }

  /**
   * Refresh invoices (reload page 1)
   */
  async refreshInvoices() {
    await this.loadMyInvoices(1);
  }

  // ========================================
  // INVOICE DETAILS
  // ========================================

  /**
   * Get invoice by documentId
   */
  async getInvoiceById(documentId: string) {
    this.setLoading(true);
    this.setError(null);

    try {
      const invoice = await invoiceService.getInvoiceById(documentId);

      runInAction(() => {
        this.selectedInvoice = invoice;
      });

      return invoice;
    } catch (error: any) {
      this.setError(error.message || 'Không thể tải hóa đơn');
      console.error('Get invoice error:', error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Get invoice by invoice number
   */
  async getInvoiceByNumber(invoiceNumber: string) {
    this.setLoading(true);
    this.setError(null);

    try {
      const invoice = await invoiceService.getInvoiceByNumber(invoiceNumber);

      runInAction(() => {
        if (invoice) {
          this.selectedInvoice = invoice;
        }
      });

      return invoice;
    } catch (error: any) {
      this.setError(error.message || 'Không thể tìm thấy hóa đơn');
      console.error('Get invoice by number error:', error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // INVOICE GENERATION
  // ========================================

  /**
   * Generate invoice từ payment
   * Called by payment webhook hoặc manual trigger
   */
  async generateInvoiceFromPayment(
    paymentId: string,
    options: InvoiceGenerationOptions = {}
  ) {
    this.setGenerating(true);
    this.setError(null);

    try {
      const invoice = await invoiceService.generateInvoiceFromPayment(
        paymentId,
        options
      );

      runInAction(() => {
        // Add to list
        this.invoices.unshift(invoice);
        this.totalInvoices += 1;
      });

      console.log('✅ Invoice generated:', invoice.invoiceNumber);
      return invoice;
    } catch (error: any) {
      this.setError(error.message || 'Không thể tạo hóa đơn');
      console.error('Generate invoice error:', error);
      throw error;
    } finally {
      this.setGenerating(false);
    }
  }

  // ========================================
  // INVOICE ACTIONS
  // ========================================

  /**
   * Download invoice PDF
   */
  async downloadInvoicePDF(documentId: string) {
    this.setDownloading(true);
    this.setError(null);

    try {
      const blob = await invoiceService.downloadInvoicePDF(documentId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${documentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      console.log('✅ Invoice PDF downloaded');
    } catch (error: any) {
      this.setError(error.message || 'Không thể tải hóa đơn');
      console.error('Download PDF error:', error);
      throw error;
    } finally {
      this.setDownloading(false);
    }
  }

  /**
   * Resend invoice email
   */
  async resendInvoiceEmail(documentId: string) {
    this.setSendingEmail(true);
    this.setError(null);

    try {
      await invoiceService.resendInvoiceEmail(documentId);
      console.log('✅ Invoice email resent');
    } catch (error: any) {
      this.setError(error.message || 'Không thể gửi lại email');
      console.error('Resend email error:', error);
      throw error;
    } finally {
      this.setSendingEmail(false);
    }
  }

  /**
   * Mark invoice as paid
   */
  async markAsPaid(documentId: string) {
    this.setLoading(true);
    this.setError(null);

    try {
      await invoiceService.markAsPaid(documentId);

      runInAction(() => {
        // Update trong list
        const invoice = this.invoices.find(inv => inv.documentId === documentId);
        if (invoice) {
          invoice.invoiceStatus = 'paid';
        }

        // Update selected invoice
        if (this.selectedInvoice?.documentId === documentId) {
          this.selectedInvoice.invoiceStatus = 'paid';
        }
      });

      console.log('✅ Invoice marked as paid');
    } catch (error: any) {
      this.setError(error.message || 'Không thể cập nhật trạng thái');
      console.error('Mark as paid error:', error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // FILTERS & SEARCH
  // ========================================

  /**
   * Update filters và reload
   */
  updateFilters(newFilters: Partial<InvoiceFilters>) {
    this.filters = {
      ...this.filters,
      ...newFilters
    };

    // Reload với filter mới
    this.loadMyInvoices(1);
  }

  /**
   * Clear all filters
   */
  clearFilters() {
    this.filters = {};
    this.loadMyInvoices(1);
  }

  /**
   * Remove một filter
   */
  removeFilter(filterKey: keyof InvoiceFilters) {
    const newFilters = { ...this.filters };
    delete newFilters[filterKey];
    this.filters = newFilters;
    this.loadMyInvoices(1);
  }

  /**
   * Search invoices by query
   */
  searchInvoices(query: string) {
    this.updateFilters({ search: query });
  }

  // ========================================
  // STATISTICS
  // ========================================

  /**
   * Load invoice statistics
   */
  async loadStats() {
    try {
      const invoices = this.invoices.length > 0 
        ? this.invoices 
        : (await invoiceService.getMyInvoices({}, 1, 1000)).invoices;

      const totalAmount = invoices.reduce((sum, inv) => sum + inv.total, 0);
      const paidAmount = invoices
        .filter(inv => inv.invoiceStatus === 'paid')
        .reduce((sum, inv) => sum + inv.total, 0);
      const unpaidAmount = totalAmount - paidAmount;

      // This month stats
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const thisMonthInvoices = invoices.filter(
        inv => new Date(inv.issuedAt) >= startOfMonth
      );
      const thisMonthAmount = thisMonthInvoices.reduce(
        (sum, inv) => sum + inv.total, 
        0
      );

      runInAction(() => {
        this.stats = {
          totalInvoices: invoices.length,
          totalAmount,
          paidAmount,
          unpaidAmount,
          thisMonthInvoices: thisMonthInvoices.length,
          thisMonthAmount
        };
      });
    } catch (error) {
      console.error('Load stats error:', error);
    }
  }

  // ========================================
  // COMPUTED PROPERTIES
  // ========================================

  /**
   * Get unpaid invoices
   */
  get unpaidInvoices(): Invoice[] {
    return this.invoices.filter(
      inv => inv.invoiceStatus !== 'paid' && inv.invoiceStatus !== 'void'
    );
  }

  /**
   * Get paid invoices
   */
  get paidInvoices(): Invoice[] {
    return this.invoices.filter(inv => inv.invoiceStatus === 'paid');
  }

  /**
   * Get recent invoices (last 30 days)
   */
  get recentInvoices(): Invoice[] {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return this.invoices.filter(
      inv => new Date(inv.issuedAt) >= thirtyDaysAgo
    );
  }

  // ========================================
  // UTILITIES
  // ========================================

  /**
   * Format currency VND
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  }

  /**
   * Format date
   */
  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('vi-VN');
  }

  // ========================================
  // STATE SETTERS
  // ========================================

  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setGenerating(generating: boolean) {
    this.isGenerating = generating;
  }

  setDownloading(downloading: boolean) {
    this.isDownloading = downloading;
  }

  setSendingEmail(sending: boolean) {
    this.isSendingEmail = sending;
  }

  setError(error: string | null) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  // ========================================
  // RESET
  // ========================================

  reset() {
    this.invoices = [];
    this.selectedInvoice = null;
    this.isLoading = false;
    this.isGenerating = false;
    this.isDownloading = false;
    this.isSendingEmail = false;
    this.error = null;
    this.currentPage = 1;
    this.pageSize = 20;
    this.totalInvoices = 0;
    this.totalPages = 1;
    this.hasMore = true;
    this.filters = {};
    this.stats = null;
  }
}

export default InvoiceStore;
